import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { MifClientTokenStorageService } from '@shared/auth/mif-client-token-storage.service';
import { MifSubscriptionAuthService } from '@shared/auth/mif-subscription.auth.service';
import { CustomerService } from '@shared/webapi/services/customer.service';
import { of } from 'rxjs';
import { catchError, exhaustMap, map, switchMap, take, tap } from 'rxjs/operators';
import { clientActions } from './client.actions';
import { isProspectCodeRequest, parseRequestCodeClientError, parseValidateCodeClientError } from './client.helpers';
import { ClientAuthorizationStatus } from './client.models';
import { selectClientAuthRequest } from './client.selectors';

@Injectable()
export class ClientEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly mifClientTokenStorageService = inject(MifClientTokenStorageService);
  private readonly mifSubscriptionAuthService = inject(MifSubscriptionAuthService);
  private readonly customerService = inject(CustomerService);

  submitClientAuthentication = createEffect(() =>
    this.actions$.pipe(
      ofType(clientActions.submitClientAuthentication),
      switchMap(({ request }) =>
        this.mifSubscriptionAuthService.requestCodeForClient(request).pipe(
          map(authResponse => clientActions.submitClientAuthenticationSuccess({ authResponse })),
          catchError(error =>
            of(
              clientActions.submitClientAuthenticationFailure({
                authError: parseRequestCodeClientError(error)
              })
            )
          )
        )
      )
    )
  );

  submitProspectAuthentication = createEffect(() =>
    this.actions$.pipe(
      ofType(clientActions.submitProspectAuthentication),
      switchMap(({ request }) =>
        this.mifSubscriptionAuthService.requestCodeForProspect(request).pipe(
          map(authResponse => clientActions.submitClientAuthenticationSuccess({ authResponse })),
          catchError(error =>
            of(
              clientActions.submitClientAuthenticationFailure({
                authError: parseRequestCodeClientError(error)
              })
            )
          )
        )
      )
    )
  );

  validateClientCode = createEffect(() =>
    this.actions$.pipe(
      ofType(clientActions.validateClientCode),
      exhaustMap(({ request }) =>
        this.mifSubscriptionAuthService.validateCode(request).pipe(
          switchMap(tokenResponse =>
            this.store.select(selectClientAuthRequest).pipe(
              take(1),
              switchMap(authRequest => {
                if (!authRequest) {
                  const resumeUserId = this.customerService.userId?.trim();

                  return of(
                    clientActions.validateClientCodeSuccess({ tokenResponse }),
                    resumeUserId
                      ? clientActions.authorizeValidatedClientByUserId({
                          userId: resumeUserId
                        })
                      : clientActions.authorizeValidatedClientFailure({
                          status: ClientAuthorizationStatus.Unauthorized
                        })
                  );
                }

                if (isProspectCodeRequest(authRequest)) {
                  return of(
                    clientActions.validateClientCodeSuccess({ tokenResponse }),
                    clientActions.authorizeValidatedProspectSuccess({
                      status: ClientAuthorizationStatus.Authorized
                    })
                  );
                }

                return of(
                  clientActions.validateClientCodeSuccess({ tokenResponse }),
                  clientActions.authorizeValidatedClient({
                    request: authRequest
                  })
                );
              })
            )
          ),
          catchError(error =>
            of(
              clientActions.validateClientCodeFailure({
                authError: parseValidateCodeClientError(error)
              })
            )
          )
        )
      )
    )
  );

  persistValidatedClientToken = createEffect(
    () =>
      this.actions$.pipe(
        ofType(clientActions.validateClientCodeSuccess),
        tap(({ tokenResponse }) => {
          if (!tokenResponse.accessToken) {
            this.mifClientTokenStorageService.clearToken();
            return;
          }

          this.mifClientTokenStorageService.saveToken(tokenResponse.accessToken);
        })
      ),
    { dispatch: false }
  );

  clearValidatedClientToken = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          clientActions.setClientType,
          clientActions.cancelClientAuthentication,
          clientActions.submitClientAuthentication,
          clientActions.submitProspectAuthentication,
          clientActions.validateClientCodeFailure,
          clientActions.authorizeValidatedClientFailure,
          clientActions.reset
        ),
        tap(() => {
          this.mifClientTokenStorageService.clearToken();
        })
      ),
    { dispatch: false }
  );

  authorizeValidatedClient = createEffect(() =>
    this.actions$.pipe(
      ofType(clientActions.authorizeValidatedClient),
      switchMap(({ request }) =>
        this.customerService
          .getClient(request.clientId as string)
          .then(profile => {
            if (!profile) {
              return clientActions.authorizeValidatedClientFailure({
                status: ClientAuthorizationStatus.Unauthorized
              });
            }

            if (profile.isLegallyCapable === false) {
              return clientActions.authorizeValidatedClientFailure({
                status: ClientAuthorizationStatus.Blocked
              });
            }

            return clientActions.authorizeValidatedClientSuccess({
              profile,
              status: ClientAuthorizationStatus.Authorized
            });
          })
          .catch(() =>
            clientActions.authorizeValidatedClientFailure({
              status: ClientAuthorizationStatus.Unauthorized
            })
          )
      )
    )
  );

  authorizeValidatedClientByUserId = createEffect(() =>
    this.actions$.pipe(
      ofType(clientActions.authorizeValidatedClientByUserId),
      switchMap(({ userId }) =>
        this.customerService
          .getUserData(userId)
          .then(profile => {
            if (!profile) {
              return clientActions.authorizeValidatedClientFailure({
                status: ClientAuthorizationStatus.Unauthorized
              });
            }

            if (profile.isLegallyCapable === false) {
              return clientActions.authorizeValidatedClientFailure({
                status: ClientAuthorizationStatus.Blocked
              });
            }

            return clientActions.authorizeValidatedClientSuccess({
              profile,
              status: ClientAuthorizationStatus.Authorized
            });
          })
          .catch(() =>
            clientActions.authorizeValidatedClientFailure({
              status: ClientAuthorizationStatus.Unauthorized
            })
          )
      )
    )
  );
}
